<template>
  <div
    v-if="showFeaturedCollection"
    :class="[
      'highlighted-featured-collection',
      isLockedContent &&
        'highlighted-featured-collection__locked-content-wrapper',
      smallView && 'highlighted-featured-collection--small-view',
    ]"
  >
    <template v-if="isFetching && !collection">
      <ActionLoader
        name="line-scale"
        color="#ef7d22"
        class="highlighted-featured-collection__loader"
      />
    </template>
    <template v-else>
      <LockedContentHeader
        v-if="isLockedContent"
        class="highlighted-featured-collection__locked-content"
      />
      <div class="highlighted-featured-collection__disclaimer-wrapper">
        <div class="highlighted-featured-collection__disclaimer">
          New content every month!
        </div>
        <div
          class="highlighted-featured-collection__content-type-separator highlighted-featured-collection__content-type-separator--hide-mobile"
        ></div>
        <div class="highlighted-featured-collection__content-type">
          <LearningAreaLabel
            v-for="la in collection.learning_areas"
            :key="la.id"
            :learningArea="la.name"
            uppercase
            class="highlighted-featured-collection__content-type-la"
          />
          <div
            class="highlighted-featured-collection__content-type-separator"
          ></div>
          <Difficulty
            :difficulty="collection.difficulty"
            uppercase
            :bold="false"
            :alwaysVisible="true"
            class="highlighted-featured-collection__content-type-difficulty"
          />
        </div>
      </div>
      <div class="highlighted-featured-collection__title">
        {{ collection.name }}
      </div>
      <div
        :class="[
          'highlighted-featured-collection__content',
          smallView && 'highlighted-featured-collection__content--small-view',
        ]"
      >
        <div class="highlighted-featured-collection__description">
          {{ collection.description }}
        </div>
        <div
          v-if="featuredLabs && !smallView"
          class="highlighted-featured-collection__labs"
        >
          <LabsCollectionCardSmall
            v-for="lab in featuredLabs"
            :key="lab.uuid"
            :lab="lab"
            :isLockedContent="isLockedContent"
            :collectionId="featuredCollectionId"
          />
        </div>
        <div class="highlighted-featured-collection__explore">
          <router-link
            :to="{
              name: 'collection-detail',
              params: { id: collection.objectID || collection.id },
            }"
            class="btn btn--primary btn--responsive btn--full-mobile"
          >
            Explore collection
          </router-link>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Difficulty from '@/components/properties/Difficulty.vue'
import LearningAreaLabel from '@/components/properties/LearningAreaLabel.vue'
import LabsCollectionCardSmall from '@/components/lab-experience/LabsCollectionCardSmall.vue'
import LockedContentHeader from '@/components/properties/LockedContentHeader.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { collectionCommons } from '@/helpers/mixins'
import CONSTANTS from '@/constants'

export default {
  name: 'HighlightedFeaturedCollection',
  components: {
    Difficulty,
    LearningAreaLabel,
    LabsCollectionCardSmall,
    LockedContentHeader,
    ActionLoader,
  },
  mixins: [collectionCommons],
  props: {
    featuredCollectionId: {
      type: String,
      required: false,
      default: CONSTANTS.LABS_COLLECTIONS.VULNERABILITIES,
    },
    smallView: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      featuredLabs: [],
      isFetching: false,
      isLockedContent: true,
    }
  },
  computed: {
    collection() {
      return this.highlightedCollection ? this.highlightedCollection : null
    },
    showFeaturedCollection() {
      return this.currentCollection
        ? this.featuredCollectionId !== this.currentCollection.id
        : this.featuredCollectionId
    },
  },
  async created() {
    this.isFetching = true
    if (this.featuredCollectionId !== this.collection?.id) {
      await this.getHighlightedCollectionById(this.featuredCollectionId)
    }
    if (!this.smallView) {
      this.getFeaturedLabsInformation()
    }
    this.setIsLockedContent()
    this.isFetching = false
  },
  methods: {
    ...mapActions('labExperience', ['getHighlightedCollectionById']),
    getFeaturedLabsInformation() {
      const labs = this.getLabsFromCollection(this.collection)
      const featuredLabsIds = this.collection.featured_labs.map(fl => fl.id)
      this.featuredLabs = labs.filter(lab => featuredLabsIds.includes(lab.uuid))
    },
    setIsLockedContent() {
      this.isLockedContent = this.lockedContent(this.collection)
    },
  },
}
</script>

<style lang="scss" scoped>
.highlighted-featured-collection {
  $this: &;
  background: var(--glass-dark-gray);
  background: var(--skill-dive-highlighted-featured-coll),
    var(--glass-dark-gray);
  background-position: inherit;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 25px;
  padding: 20px;
  display: flex;
  flex-flow: column;
  gap: 10px;
  text-align: left;
  @include mobile {
    gap: 15px;
    height: 100%;
    padding: 40px 30px;
  }
  @include desktop {
    padding: 60px 70px;
  }
  &--small-view {
    @include desktop {
      padding: 60px;
    }
  }
  &__disclaimer {
    overflow: hidden;
    color: var(--positive-light);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 500;
    line-height: rem(20px);
    &-wrapper {
      width: 100%;
      @include mobile {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 10px;
      }
    }
  }
  &__title {
    font-size: rem(30px);
    font-style: normal;
    font-weight: 500;
    line-height: rem(32px);
    width: 100%;
    @include mobile {
      font-size: rem(54px);
      font-weight: 700;
    }
  }
  &__description {
    font-size: rem(12px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(14px);
    width: 100%;
    @include mobile {
      color: var(--dusty-gray);
      font-size: rem(16px);
      line-height: rem(24px);
    }
    @include desktop {
      width: 75%;
    }
  }
  &__content {
    display: flex;
    flex-flow: column;
    height: 100%;
    justify-content: space-between;
    &-type {
      display: flex;
      gap: 10px;
      white-space: nowrap;
      &-separator {
        width: 1px;
        height: 30px;
        background: var(--glass-solid-mid-gray);
        &--hide-mobile {
          display: none;
          @include mobile {
            display: block;
          }
        }
      }
      &-la,
      &-difficulty {
        font-size: rem(12px);
      }
    }
    &--small-view {
      display: flex;
      justify-content: space-between;
      gap: 20px;
      @include desktop {
        flex-flow: nowrap;
        #{$this}__description {
          width: 60%;
        }
      }
    }
  }
  &__explore {
    @include mobile {
      align-self: flex-end;
    }
  }
  &__labs {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 5px;
    padding: 5px 0;
    @include desktop {
      grid-template-columns: repeat(2, 1fr);
      grid-gap: 10px;
      padding: 25px 0;
    }
  }
  &__locked-content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    border-radius: 20px 20px 0 0;
    &-wrapper {
      padding-top: 50px;
      position: relative;
    }
  }
  &__loader {
    margin: auto;
  }
}
</style>
